import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { isReservedSegment } from '@app/shared/utils/resolve-organizacao';
import criptografar from '@app/shared/rotinas/criptografar';
import descriptografar from '@app/shared/rotinas/descriptografar';
import jwt from 'jwt-decode';
import { tap } from 'rxjs';
import { BaseApiService } from '../base-api.service';
import { AuthKeys } from './auth.enum';
import {
  Login,
  LoginResponse,
  PostCliente,
  PostClienteResponse,
  RedefinirSenhaPrimeiroAcessoRequest,
} from './auth.models';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseApiService {
  currentStorage = sessionStorage;

  constructor(private readonly router: Router) {
    super('clientes');
  }

  register(data: PostCliente) {
    return this.post<PostClienteResponse>(data, 'sign-up').pipe(
      tap((response) => this.setUserData(response))
    );
  }

  login(data: Login, returnUrl?: string) {
    return this.post<LoginResponse>(data, 'sign-in').pipe(
      tap((response) => {
        this.setUserData(response);

        if (response?.data?.mustChangePassword) {
          this.redirectToForceChangePassword(returnUrl);
        }
      })
    );
  }

  redefinirSenhaPrimeiroAcesso(data: RedefinirSenhaPrimeiroAcessoRequest) {
    return this.post<LoginResponse>(data, 'redefinir-senha-primeiro-acesso').pipe(
      tap((response) => this.setUserData(response))
    );
  }

  get isLogged(): boolean {
    return typeof this.findInStorageByKey(AuthKeys.INFO_TOKEN) === 'string';
  }

  findInStorageByKey(key: string) {
    this.currentStorage = localStorage.getItem(key)
      ? localStorage
      : sessionStorage;
    return this.currentStorage.getItem(key);
  }

  logout() {
    this.findInStorageByKey(AuthKeys.INFO_TOKEN);
    this.currentStorage.removeItem(AuthKeys.INFO_TOKEN);
    this.currentStorage.removeItem(AuthKeys.INFO_USER);
    this.currentStorage.removeItem(AuthKeys.MUST_CHANGE_PASSWORD);
    const currentRoute = location.pathname;
    const lastTwoRoutes = currentRoute.split('/').slice(-2).join('/');
    this.router.navigate([currentRoute.replace(lastTwoRoutes, 'assinatura')]);
  }

  get mustChangePassword(): boolean {
    const storage = this.findInStorageByKey(AuthKeys.INFO_TOKEN) ? this.currentStorage : sessionStorage;
    return storage.getItem(AuthKeys.MUST_CHANGE_PASSWORD) === '1';
  }

  clearMustChangePassword() {
    this.currentStorage.removeItem(AuthKeys.MUST_CHANGE_PASSWORD);
  }

  redirectToForceChangePassword(returnUrl?: string) {
    const organizacao = sessionStorage.getItem('router') || location.pathname.split('/')[1] || '';

    if (returnUrl) {
      sessionStorage.setItem('force_change_returnUrl', returnUrl);
    }

    if (organizacao && !isReservedSegment(organizacao)) {
      const extras: any = {};
      if (returnUrl) {
        extras.queryParams = { returnUrl };
      }
      this.router.navigate([`/${organizacao}/auth/force-change-password`], extras);
    }
  }

  setUserData(session: any) {
    this.currentStorage.setItem(
      AuthKeys.INFO_TOKEN,
      criptografar(session.data.token)
    );

    const mustChangePassword = Boolean(session?.data?.mustChangePassword);
    this.currentStorage.setItem(
      AuthKeys.MUST_CHANGE_PASSWORD,
      mustChangePassword ? '1' : '0'
    );

    const userData: any = jwt(session.data.token);
    this.currentStorage.setItem(
      AuthKeys.INFO_USER,
      criptografar(
        JSON.stringify({
          nome: userData.data.nome,
          codigoCliente: userData.data.clientId,
          codigoPessoa: userData.data.codigoPessoa,
          dataNascimento: userData.data.dataDeNascimento,
          email: userData.data.email,
          telefone: userData.data.telefone,
        })
      )
    );
  }

  getUserData() {
    const userData = this.findInStorageByKey(AuthKeys.INFO_USER);
    return userData ? JSON.parse(descriptografar(userData)) : null;
  }
}
